<template>
  <view v-if="visible" class="loading-overlay">
    <view class="loading-container">
      <!-- 游戏风格旋转动画 -->
      <view class="game-spinner">
        <view class="spinner-core"></view>
        <view class="spinner-ring ring-1"></view>
        <view class="spinner-ring ring-2"></view>
        <view class="spinner-ring ring-3"></view>
        <view class="energy-particles">
          <view class="particle particle-1"></view>
          <view class="particle particle-2"></view>
          <view class="particle particle-3"></view>
          <view class="particle particle-4"></view>
        </view>
      </view>

      <!-- 游戏风格加载文字 -->
      <view class="game-text-container">
        <text class="loading-text">{{ text }}</text>
        <view class="text-glow"></view>
      </view>

      <!-- 游戏风格进度条 -->
      <view class="game-progress">
        <view class="progress-bar">
          <view class="progress-fill"></view>
          <view class="progress-glow"></view>
        </view>
        <!-- <view class="progress-dots">
          <view class="progress-dot active"></view>
          <view class="progress-dot"></view>
          <view class="progress-dot"></view>
          <view class="progress-dot"></view>
        </view> -->
      </view>
    </view>
  </view>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const visible = ref(false)
const text = ref('Loading...')
const isWorking = ref(false) // 工作状态标记
let workTimer = null // 工作计时器
let workStartTime = null // 工作开始时间

// 全局实例管理
let globalInstance = null
let currentInstance = null

// 全局计时器清理函数
function clearAllTimers() {
  if (workTimer) {
    // console.log('CustomLoading: 全局清理计时器', { timerId: workTimer })
    clearTimeout(workTimer)
    workTimer = null
  }
}

function show(options = {}) {
  // console.log('CustomLoading.show called', options)

  // 如果已经有实例在工作，拒绝新的请求
  if (globalInstance && globalInstance !== currentInstance) {
    // console.log('CustomLoading: 已有实例在工作，拒绝新请求')
    return
  }

  // 如果当前实例已经在工作，拒绝重复调用
  if (isWorking.value) {
    // console.log('CustomLoading: 当前实例已在工作，拒绝重复调用')
    return
  }

  // 先清除可能存在的旧计时器
  clearAllTimers()

  // 设置全局实例
  globalInstance = currentInstance

  text.value = options.title || 'Loading...'
  visible.value = true
  isWorking.value = true
  workStartTime = Date.now()

  // 设置工作计时器，防止长时间卡住
  const timeout = options.timeout || 30000 // 默认30秒超时
  workTimer = setTimeout(() => {
    // console.log('CustomLoading: 工作超时，自动隐藏', {
    //   timeout,
    //   workDuration: Date.now() - workStartTime,
    //   isWorking: isWorking.value,
    //   visible: visible.value,
    //   currentTimerId: workTimer,
    // })

    // 只有在仍然处于工作状态时才执行隐藏
    if (isWorking.value && visible.value) {
      // console.log('CustomLoading: 执行超时隐藏')
      hide()
    } else {
      console.log('CustomLoading: 超时但状态已改变，跳过隐藏')
    }
  }, timeout)

  // console.log('CustomLoading: 开始工作', {
  //   timeout,
  //   startTime: workStartTime,
  //   timerId: workTimer,
  // })
}

function hide() {
  // console.log('CustomLoading.hide called', {
  //   isWorking: isWorking.value,
  //   visible: visible.value,
  //   hasTimer: !!workTimer,
  //   workDuration: workStartTime ? Date.now() - workStartTime : 0,
  // })

  // 清除计时器
  clearAllTimers()

  visible.value = false
  isWorking.value = false
  workStartTime = null

  // 清除全局实例引用
  if (globalInstance === currentInstance) {
    globalInstance = null
  }

  // console.log('CustomLoading: 工作结束')
}

// 组件挂载时设置全局方法
onMounted(() => {
  // 设置当前实例引用
  currentInstance = { show, hide, isWorking }

  // 将当前实例的方法挂载到 uni 对象上
  uni.$loading = {
    show: (options = {}) => {
      show(options)
    },
    hide: () => {
      hide()
    },
  }
  // console.log('CustomLoading: 全局方法已挂载到 uni.$loading')
})

// 组件卸载时清理
onUnmounted(() => {
  // console.log('CustomLoading: 组件卸载开始')

  // 清除计时器
  clearAllTimers()

  // 如果当前实例是全局实例，清除引用
  if (globalInstance === currentInstance) {
    globalInstance = null
  }

  // 清除全局方法
  if (uni.$loading) {
    delete uni.$loading
  }

  // console.log('CustomLoading: 组件卸载，已清理资源')
})

// 暴露方法给外部使用
defineExpose({
  show,
  hide,
  isWorking: () => isWorking.value,
})
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
}

.loading-container {
  display: flex;
  flex-direction: column;
  gap: 25px;
  align-items: center;
  min-width: 280px;
  padding: 40px;
  background: linear-gradient(145deg, rgba(20, 20, 40, 0.95), rgba(30, 30, 60, 0.95));
  border: 2px solid rgba(0, 255, 255, 0.3);
  border-radius: 20px;
  box-shadow:
    0 0 30px rgba(0, 255, 255, 0.2),
    inset 0 0 20px rgba(0, 255, 255, 0.1),
    0 10px 40px rgba(0, 0, 0, 0.5);
}
/* 游戏风格旋转动画 */
.game-spinner {
  position: relative;
  width: 80px;
  height: 80px;
}

.spinner-core {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  background: radial-gradient(circle, #00ffff, #0080ff);
  border-radius: 50%;
  box-shadow:
    0 0 20px #00ffff,
    inset 0 0 10px rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  animation: core-pulse 2s ease-in-out infinite;
}

.spinner-ring {
  position: absolute;
  border: 2px solid transparent;
  border-radius: 50%;
  animation: game-spin 3s linear infinite;
}

.ring-1 {
  width: 100%;
  height: 100%;
  border-top: 2px solid #00ffff;
  border-right: 2px solid #00ffff;
  animation-duration: 2s;
}

.ring-2 {
  top: 10%;
  left: 10%;
  width: 80%;
  height: 80%;
  border-bottom: 2px solid #ff00ff;
  border-left: 2px solid #ff00ff;
  animation-duration: 2.5s;
  animation-direction: reverse;
}

.ring-3 {
  top: 20%;
  left: 20%;
  width: 60%;
  height: 60%;
  border-top: 2px solid #ffff00;
  border-bottom: 2px solid #ffff00;
  animation-duration: 3s;
}

.energy-particles {
  position: absolute;
  width: 100%;
  height: 100%;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: #00ffff;
  border-radius: 50%;
  box-shadow: 0 0 8px #00ffff;
}

.particle-1 {
  top: 10%;
  left: 50%;
  animation: particle-orbit 4s linear infinite;
}

.particle-2 {
  top: 50%;
  right: 10%;
  animation: particle-orbit 4s linear infinite 1s;
}

.particle-3 {
  bottom: 10%;
  left: 50%;
  animation: particle-orbit 4s linear infinite 2s;
}

.particle-4 {
  top: 50%;
  left: 10%;
  animation: particle-orbit 4s linear infinite 3s;
}

@keyframes game-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes core-pulse {
  0%,
  100% {
    box-shadow:
      0 0 20px #00ffff,
      inset 0 0 10px rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    box-shadow:
      0 0 30px #00ffff,
      inset 0 0 15px rgba(255, 255, 255, 0.5);
    transform: translate(-50%, -50%) scale(1.2);
  }
}

@keyframes particle-orbit {
  0% {
    transform: rotate(0deg) translateX(40px) rotate(0deg);
  }
  100% {
    transform: rotate(360deg) translateX(40px) rotate(-360deg);
  }
}
/* 游戏风格文字 */
.game-text-container {
  position: relative;
  text-align: center;
}

.loading-text {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #00ffff;
  text-align: center;
  text-shadow:
    0 0 10px #00ffff,
    0 0 20px #00ffff,
    0 0 30px #00ffff;
  animation: text-glow 2s ease-in-out infinite alternate;
}

.text-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.3), transparent);
  animation: text-scan 3s linear infinite;
}
/* 游戏风格进度条 */
.game-progress {
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
  width: 100%;
}

.progress-bar {
  position: relative;
  width: 200px;
  height: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 4px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00ffff, #0080ff, #00ffff);
  background-size: 200% 100%;
  box-shadow: 0 0 10px #00ffff;
  animation: progress-flow 2s linear infinite;
}

.progress-glow {
  position: absolute;
  top: -2px;
  left: 0;
  width: 100%;
  height: 12px;
  background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.5), transparent);
  animation: progress-scan 3s linear infinite;
}

.progress-dots {
  display: flex;
  gap: 12px;
  align-items: center;
}

.progress-dot {
  width: 8px;
  height: 8px;
  background: rgba(0, 255, 255, 0.3);
  border-radius: 50%;
  transition: all 0.3s ease;
}

.progress-dot.active {
  background: #00ffff;
  box-shadow: 0 0 10px #00ffff;
  animation: dot-pulse 1s ease-in-out infinite;
}

@keyframes text-glow {
  0% {
    text-shadow:
      0 0 10px #00ffff,
      0 0 20px #00ffff,
      0 0 30px #00ffff;
  }
  100% {
    text-shadow:
      0 0 15px #00ffff,
      0 0 25px #00ffff,
      0 0 35px #00ffff;
  }
}

@keyframes text-scan {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes progress-flow {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
}

@keyframes progress-scan {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes dot-pulse {
  0%,
  100% {
    box-shadow: 0 0 10px #00ffff;
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 15px #00ffff;
    transform: scale(1.3);
  }
}
/* 响应式设计 */
@media (max-width: 480px) {
  .loading-container {
    min-width: 240px;
    padding: 30px;
    margin: 20px;
  }

  .game-spinner {
    width: 60px;
    height: 60px;
  }

  .spinner-core {
    width: 15px;
    height: 15px;
  }

  .loading-text {
    font-size: 16px;
  }

  .progress-bar {
    width: 160px;
  }

  .progress-dot {
    width: 6px;
    height: 6px;
  }
}
/* 游戏主题增强 */
@media (prefers-color-scheme: dark) {
  .loading-overlay {
    background: linear-gradient(135deg, #000000 0%, #0a0a0a 50%, #1a1a2e 100%);
  }

  .loading-container {
    background: linear-gradient(145deg, rgba(10, 10, 20, 0.98), rgba(20, 20, 40, 0.98));
    border: 2px solid rgba(0, 255, 255, 0.4);
    box-shadow:
      0 0 40px rgba(0, 255, 255, 0.3),
      inset 0 0 30px rgba(0, 255, 255, 0.15),
      0 15px 50px rgba(0, 0, 0, 0.7);
  }

  .loading-text {
    color: #00ffff;
    text-shadow:
      0 0 15px #00ffff,
      0 0 25px #00ffff,
      0 0 35px #00ffff;
  }

  .spinner-core {
    background: radial-gradient(circle, #00ffff, #0080ff);
    box-shadow:
      0 0 25px #00ffff,
      inset 0 0 15px rgba(255, 255, 255, 0.4);
  }

  .ring-1 {
    border-top-color: #00ffff;
    border-right-color: #00ffff;
  }

  .ring-2 {
    border-bottom-color: #ff00ff;
    border-left-color: #ff00ff;
  }

  .ring-3 {
    border-top-color: #ffff00;
    border-bottom-color: #ffff00;
  }

  .particle {
    background: #00ffff;
    box-shadow: 0 0 12px #00ffff;
  }
}
</style>
